import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, CheckCircle2, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Logo from "../components/Logo";

const STEPS = [
  { n: 1, t: "Étape" },
  { n: 2, t: "Identité" },
  { n: 3, t: "Localisation" },
  { n: 4, t: "Engagement" },
  { n: 5, t: "Validation" },
];

export default function RegistrationPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [ev, setEv] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [selectedStage, setSelectedStage] = useState("");
  const [s1, setS1] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [s2, setS2] = useState({ country: "France", city: "", nationality: "Congolaise (RDC)" });
  const [s3, setS3] = useState({
    is_political_party_member: false, political_party_name: "",
    is_association_member: false, association_name: "",
    function: "",
  });
  const [s4, setS4] = useState({ consent_rgpd: false, consent_news: true });

  useEffect(() => {
    api.get(`/events/${slug}`).then((r) => {
      setEv(r.data);
      // If no stages → skip step 1 (single-stage event)
      if (!(r.data.stages || []).length) setStep(2);
    }).catch(() => toast.error("Événement introuvable"));
  }, [slug]);

  const hasStages = (ev?.stages?.length || 0) > 0;

  const canNext = () => {
    if (step === 1) return !hasStages || !!selectedStage;
    if (step === 2) return s1.first_name && s1.last_name && s1.phone && /\S+@\S+/.test(s1.email);
    if (step === 3) return s2.country && s2.city && s2.nationality;
    if (step === 4) return true;
    if (step === 5) return s4.consent_rgpd;
  };

  const submit = async () => {
    if (!s4.consent_rgpd) { toast.error("Vous devez accepter le consentement RGPD"); return; }
    setSubmitting(true);
    try {
      const payload = {
        event_slug: slug,
        selected_stage: hasStages ? selectedStage : null,
        step1: s1, step2: s2,
        step3: {
          ...s3,
          political_party_name: s3.is_political_party_member ? s3.political_party_name : null,
          association_name: s3.is_association_member ? s3.association_name : null,
          function: s3.function || null,
        },
        consent_rgpd: s4.consent_rgpd, consent_news: s4.consent_news,
      };
      const r = await api.post("/registrations", payload);
      nav(`/inscription-confirmee/${r.data.registration_number}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'inscription");
    } finally { setSubmitting(false); }
  };

  if (!ev) return <div className="p-12 text-foreground/60">Chargement…</div>;

  const visibleSteps = hasStages ? STEPS : STEPS.slice(1);
  const fmt = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }); } catch { return d; } };

  return (
    <div className="bg-bone min-h-screen" data-testid="registration-page">
      <section className="bg-ink text-white py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 flex items-start gap-5">
          <Logo className="w-14 h-14 shrink-0 hidden sm:block" />
          <div>
            <div className="eyebrow eyebrow-on-dark">Inscription officielle</div>
            <h1 className="font-serif text-3xl lg:text-4xl mt-3 leading-tight">{ev.title}</h1>
            <p className="mt-2 text-white/70 text-sm">{ev.theme}</p>
            {ev.hide_location && (
              <p className="mt-2 text-xs text-gold italic">Le lieu exact sera communiqué après validation de votre inscription.</p>
            )}
          </div>
        </div>
      </section>

      {/* Progress */}
      <section className="bg-white border-b border-border sticky top-[64px] z-30">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex items-center justify-between gap-2">
            {visibleSteps.map((s, i) => (
              <div key={s.n} className="flex items-center gap-3 flex-1">
                <div className={`w-9 h-9 grid place-items-center rounded-full text-sm font-semibold shrink-0 transition-colors ${
                  step > s.n ? "bg-gold text-ink" : step === s.n ? "bg-ink text-white" : "bg-bone text-foreground/40 border border-border"
                }`} data-testid={`step-indicator-${s.n}`}>
                  {step > s.n ? <Check className="w-4 h-4" /> : (hasStages ? s.n : s.n - 1)}
                </div>
                <div className="hidden sm:block">
                  <div className="text-[0.65rem] uppercase tracking-[0.18em] font-semibold text-foreground/50">Étape {hasStages ? s.n : s.n - 1}</div>
                  <div className={`text-sm font-semibold ${step >= s.n ? "text-ink" : "text-foreground/40"}`}>{s.t}</div>
                </div>
                {i < visibleSteps.length - 1 && <div className={`flex-1 h-px ${step > s.n ? "bg-gold" : "bg-border"}`} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="bg-white border border-border rounded-sm p-8 lg:p-12 animate-fade-up" key={step}>
            {step === 1 && hasStages && (
              <div data-testid="step-1-stage">
                <div className="eyebrow text-gold-dark">Étape 1</div>
                <h2 className="font-serif text-3xl text-ink mt-3 leading-tight">À quelle étape souhaitez-vous participer ?</h2>
                <p className="mt-3 text-foreground/65 text-sm">Sélectionnez le rendez-vous européen auquel vous comptez assister.</p>
                <div className="mt-8 grid gap-3">
                  {ev.stages.map((stage, i) => {
                    const value = stage.city;
                    const active = selectedStage === value;
                    return (
                      <button key={i} onClick={() => setSelectedStage(value)} data-testid={`stage-option-${i}`}
                              className={`text-left p-5 rounded-sm border-2 transition-all relative ${
                                active ? "border-ink bg-ink/[0.03] shadow-md" : "border-border hover:border-ink/40"
                              }`}>
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <div className="text-[0.7rem] uppercase tracking-[0.18em] font-bold text-gold-dark">{stage.label || `Étape ${i + 1}`}</div>
                            <div className="font-serif text-2xl text-ink mt-1">{stage.city}</div>
                            <div className="text-sm text-foreground/65">{stage.country}</div>
                            <div className="mt-3 flex items-center gap-2 text-sm text-foreground/75">
                              <Calendar className="w-4 h-4 text-gold-dark" />
                              <span>{fmt(stage.date)}{stage.time && ` · ${stage.time}`}</span>
                            </div>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 grid place-items-center shrink-0 ${active ? "border-ink bg-ink" : "border-border"}`}>
                            {active && <Check className="w-3.5 h-3.5 text-white" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {step === 2 && (
              <div data-testid="step-2">
                <div className="eyebrow text-gold-dark">Étape {hasStages ? 2 : 1}</div>
                <h2 className="font-serif text-3xl text-ink mt-3 leading-tight">Identité</h2>
                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                  <Input label="Nom" v={s1.last_name} on={(v) => setS1({ ...s1, last_name: v })} test="reg-last-name" />
                  <Input label="Prénom" v={s1.first_name} on={(v) => setS1({ ...s1, first_name: v })} test="reg-first-name" />
                  <Input label="Téléphone" v={s1.phone} on={(v) => setS1({ ...s1, phone: v })} test="reg-phone" />
                  <Input label="Email" type="email" v={s1.email} on={(v) => setS1({ ...s1, email: v })} test="reg-email" />
                </div>
              </div>
            )}
            {step === 3 && (
              <div data-testid="step-3">
                <div className="eyebrow text-gold-dark">Étape {hasStages ? 3 : 2}</div>
                <h2 className="font-serif text-3xl text-ink mt-3 leading-tight">Localisation</h2>
                <p className="mt-2 text-foreground/60 text-sm">Indiquez où vous résidez.</p>
                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                  <Input label="Pays" v={s2.country} on={(v) => setS2({ ...s2, country: v })} test="reg-country" />
                  <Input label="Ville" v={s2.city} on={(v) => setS2({ ...s2, city: v })} test="reg-city" />
                  <Input label="Nationalité" v={s2.nationality} on={(v) => setS2({ ...s2, nationality: v })} test="reg-nationality" />
                </div>
              </div>
            )}
            {step === 4 && (
              <div data-testid="step-4">
                <div className="eyebrow text-gold-dark">Étape {hasStages ? 4 : 3}</div>
                <h2 className="font-serif text-3xl text-ink mt-3 leading-tight">Engagement</h2>
                <div className="mt-8 space-y-6">
                  <Toggle label="Êtes-vous membre d'un parti politique ?"
                          v={s3.is_political_party_member}
                          on={(v) => setS3({ ...s3, is_political_party_member: v })}
                          test="reg-party-toggle" />
                  {s3.is_political_party_member && (
                    <Input label="Nom du parti politique" v={s3.political_party_name}
                           on={(v) => setS3({ ...s3, political_party_name: v })} test="reg-party-name" />
                  )}
                  <Toggle label="Êtes-vous membre d'une association ?"
                          v={s3.is_association_member}
                          on={(v) => setS3({ ...s3, is_association_member: v })}
                          test="reg-asso-toggle" />
                  {s3.is_association_member && (
                    <Input label="Nom de l'association" v={s3.association_name}
                           on={(v) => setS3({ ...s3, association_name: v })} test="reg-asso-name" />
                  )}
                  <Input label="Fonction / responsabilité (optionnel)" v={s3.function}
                         on={(v) => setS3({ ...s3, function: v })} test="reg-function" />
                </div>
              </div>
            )}
            {step === 5 && (
              <div data-testid="step-5">
                <div className="eyebrow text-gold-dark">Étape {hasStages ? 5 : 4}</div>
                <h2 className="font-serif text-3xl text-ink mt-3 leading-tight">Validation</h2>
                <p className="mt-4 text-foreground/70">Vérifiez vos informations et confirmez votre inscription.</p>
                <div className="mt-6 bg-bone p-5 rounded-sm text-sm space-y-2 border border-border">
                  {hasStages && selectedStage && (
                    <div className="flex items-center gap-2 text-ink font-semibold">
                      <MapPin className="w-4 h-4 text-gold-dark" /> Étape choisie : <strong>{selectedStage}</strong>
                    </div>
                  )}
                  <div><strong>{s1.first_name} {s1.last_name}</strong> · {s1.email}</div>
                  <div className="text-foreground/70">{s2.city}, {s2.country} · {s2.nationality}</div>
                  {s3.is_political_party_member && <div className="text-foreground/70">Parti : {s3.political_party_name}</div>}
                  {s3.is_association_member && <div className="text-foreground/70">Association : {s3.association_name}</div>}
                </div>
                <div className="mt-6 space-y-3">
                  <Checkbox v={s4.consent_rgpd} on={(v) => setS4({ ...s4, consent_rgpd: v })} test="reg-consent-rgpd"
                            label="J'accepte le traitement de mes données personnelles dans le cadre du RGPD." />
                  <Checkbox v={s4.consent_news} on={(v) => setS4({ ...s4, consent_news: v })} test="reg-consent-news"
                            label="Je souhaite recevoir les actualités du Front Uni pour la RDC." />
                </div>
              </div>
            )}

            <div className="mt-10 pt-6 border-t border-border flex items-center justify-between">
              <button
                onClick={() => setStep(Math.max(hasStages ? 1 : 2, step - 1))}
                disabled={step === (hasStages ? 1 : 2)}
                className="btn-outline disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="reg-prev"
              >
                <ArrowLeft className="w-4 h-4" /> Précédent
              </button>
              {step < 5 ? (
                <button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="reg-next">
                  Suivant <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={submit} disabled={!canNext() || submitting}
                        className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed" data-testid="reg-submit">
                  {submitting ? "Inscription…" : <>Confirmer l'inscription <CheckCircle2 className="w-4 h-4" /></>}
                </button>
              )}
            </div>
          </div>
          <div className="mt-6 text-center text-sm text-foreground/60">
            <Link to={`/evenements/${slug}`} className="hover:text-ink">← Revoir le programme de l'événement</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Input({ label, v, on, type = "text", test }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-2">{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)}
             className="w-full border border-border rounded-sm p-3 focus:border-ink outline-none text-sm"
             data-testid={test} />
    </div>
  );
}
function Toggle({ label, v, on, test }) {
  return (
    <div className="flex items-center justify-between bg-bone rounded-sm p-4 border border-border">
      <span className="text-sm text-ink">{label}</span>
      <button type="button" onClick={() => on(!v)} data-testid={test}
              className={`relative w-12 h-6 rounded-full transition-colors ${v ? "bg-ink" : "bg-foreground/20"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${v ? "translate-x-6" : ""}`} />
      </button>
    </div>
  );
}
function Checkbox({ v, on, label, test }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={v} onChange={(e) => on(e.target.checked)} className="mt-1 w-4 h-4 accent-ink" data-testid={test} />
      <span className="text-sm text-foreground/80">{label}</span>
    </label>
  );
}
